import { cn } from '@/lib/utils';
import { calcularTempoTriagem, type StatusSLA } from '@/lib/tempoComercial';
import { format, parseISO } from 'date-fns';

interface TempoTriagemBadgeProps {
  entradaColunaEm: string | null | undefined;
  prazoHoras: number | 'agendamento';
  dataAgendamento?: string | null;
  className?: string;
}

/* §4 da régua das triagens (17/08): o tempo era um CHIP com borda + fundo +
   ícone de relógio + texto colorido — quatro camadas de ênfase para um dado só,
   repetidas 13 vezes por tela. Resultado: nada se destacava.
   Agora é texto nu; quem precisa de atenção ganha um dot, e quem está no prazo
   não ganha cor nenhuma. O relógio saiu — o rótulo da coluna já diz TEMPO. */
function getStatusClasses(status: StatusSLA): string {
  switch (status) {
    case 'atencao':
      // ⛔ Era ÂMBAR, que não existe na paleta da casa. "Atenção" é prazo
      // apertando — dívida chegando — e dívida é BORDÔ. Estava em todas as 13
      // linhas de todas as triagens, e era o âmbar mais visível do CRM.
      return 'font-bold text-primary';
    case 'atrasado':
      return 'font-bold text-danger';
    case 'ok':
    case 'sem_prazo':
    default:
      return 'font-semibold text-muted-foreground';
  }
}

/** Só quem precisa de atenção tem dot. No prazo não tem — é a leitura imediata. */
function Dot({ status }: { status: StatusSLA }) {
  if (status !== 'atrasado' && status !== 'atencao') return null;
  return (
    <span
      className={cn(
        'h-[5px] w-[5px] flex-shrink-0 rounded-full',
        status === 'atrasado' ? 'bg-danger' : 'bg-primary',
      )}
    />
  );
}

/**
 * Badge de tempo na triagem com cores semânticas baseadas no SLA.
 * 
 * no prazo   (< 80% do limite)  → sem cor nenhuma, é o normal
 * atenção     (80%-100%)         → BORDÔ, porque é dívida chegando
 * atrasado    (> 100%)           → danger, o semáforo real
 *
 * Para Triagem Solar Fixa (prazoHoras="agendamento"):
 * antes da instalação → sem cor · depois → danger
 *
 * ⛔ O comentário desenhava isto com 🟢🟡🔴. A lei visual da casa proíbe emoji
 * de semáforo justamente porque ele vira o desenho de quem lê o código depois —
 * e o amarelo do comentário virou `text-primary` na tela.
 */
export function TempoTriagemBadge({
  entradaColunaEm,
  prazoHoras,
  dataAgendamento,
  className,
}: TempoTriagemBadgeProps) {
  if (!entradaColunaEm) {
    return <span className="text-[10px] text-muted-foreground">-</span>;
  }

  const resultado = calcularTempoTriagem(entradaColunaEm, prazoHoras, dataAgendamento);
  const statusClasses = getStatusClasses(resultado.status);

  // Para agendamento: mostra a data de instalação como referência
  if (prazoHoras === 'agendamento' && dataAgendamento) {
    let dataFormatada = '-';
    try {
      const d = dataAgendamento.includes('T')
        ? parseISO(dataAgendamento)
        : new Date(dataAgendamento + 'T00:00:00');
      dataFormatada = format(d, 'dd/MM');
    } catch { /* empty */ }

    return (
      <span
        className={cn('inline-flex items-center gap-1 whitespace-nowrap text-[11px] tabular-nums', statusClasses, className)}
        title={`Tempo na fila: ${resultado.label} | Data instalação: ${dataFormatada}`}
        /* §6: mostrava a DATA aqui, repetindo a coluna DATA INSTALAÇÃO ao lado.
           TEMPO e' sempre tempo na fila; a data manda so' na COR (vermelho
           depois do dia da instalacao). */
      >
        <Dot status={resultado.status} />
        {resultado.label}
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex items-center gap-1 whitespace-nowrap text-[11px] tabular-nums', statusClasses, className)}
      title={
        resultado.prazoHoras
          ? `Tempo útil: ${resultado.label} | Prazo: ${resultado.prazoHoras}h | ${resultado.status === 'atrasado' ? '⚠ ATRASADO' : resultado.status === 'atencao' ? '⚡ Atenção' : '✓ No prazo'}`
          : `Tempo útil: ${resultado.label}`
      }
    >
      <Dot status={resultado.status} />
      {resultado.label}
    </span>
  );
}
